/**
 * DataGrid — thin wrapper around AgGridReact that applies FAIM2 theme + defaults.
 *
 * This component owns ZERO feature/domain logic. Pages pass their own:
 *  - rowData
 *  - columnDefs
 *  - event handlers (onRowClicked, etc.)
 *
 * All AgGridReact props are forwarded so pages have full control.
 */
import { AgGridReact, type AgGridReactProps } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';
import { faim2Theme, faim2ThemeDark } from './theme';
import { defaultColDef as sharedDefaultColDef } from './defaults';
import { useTheme } from '../../theme';

export type DataGridProps<TData = unknown> = AgGridReactProps<TData> & {
  /** CSS height for the grid container. Defaults to 600px. */
  height?: string | number;
};

export function DataGrid<TData = unknown>({
  height = 600,
  defaultColDef,
  theme,
  ...rest
}: DataGridProps<TData>) {
  const { theme: currentTheme } = useTheme();
  const gridTheme = theme ?? (currentTheme === 'theme-dark' ? faim2ThemeDark : faim2Theme);

  return (
    <div style={{ height }}>
      <AgGridReact<TData>
        theme={gridTheme}
        defaultColDef={{ ...sharedDefaultColDef, ...defaultColDef } as ColDef<TData>}
        {...rest}
      />
    </div>
  );
}
